//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  DEFAULT_PRODUCT_IMAGE_URL,
  IMAGE_TIMESTAMP_PARAM,
} from '@campaigns/constants/SamplingImageConstants';
import { EditableBVSamplingProductVariation } from '@campaigns/types/sampling/SamplingProductTypes';

/**
 * Removes the 't' (timestamp) parameter from a URL while preserving other query parameters
 * @param url - The URL to normalize
 * @returns The URL without the 't' parameter
 */
const normalizeImageUrl = (url: string | undefined): string => {
  if (!url) {
    return '';
  }
  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.delete(IMAGE_TIMESTAMP_PARAM);
    return parsedUrl.toString();
  } catch (e) {
    // If URL parsing fails, manually remove the 't' parameter
    const [base, ...queryParts] = url.split('?');
    if (!queryParts.length) {
      return base ?? '';
    }

    const queryString = queryParts.join('?');
    const params = new URLSearchParams(queryString);
    params.delete(IMAGE_TIMESTAMP_PARAM);

    const newQuery = params.toString();
    return newQuery ? `${base}?${newQuery}` : base ?? '';
  }
};

export const doImageUrlsMatch = (url1?: string, url2?: string): boolean => {
  if (!url1 || !url2) {
    return false;
  }
  return normalizeImageUrl(url1) === normalizeImageUrl(url2);
};

/** The stored system placeholder represents a missing image, not an assigned URL. */
export const getAssignedProductImageUrl = (
  url: string | undefined,
): string | undefined =>
  doImageUrlsMatch(url, DEFAULT_PRODUCT_IMAGE_URL) ? '' : url;

export const doAllVariantImagesMatch = (
  mainImageUrl?: string,
  variations?: EditableBVSamplingProductVariation[],
): boolean => {
  if (!mainImageUrl || !variations?.length) {
    return false;
  }

  return variations.every((variation) =>
    doImageUrlsMatch(variation.imgUrl, mainImageUrl),
  );
};
